import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Banner, Card, Field, Modal, ModalHeader, PageHeader, Pill, Stat, Tag, cellStyle, inputStyle, thStyle, useToast } from '@/components/ui';
import { Select } from '@/components/Select';
import { Icon, I } from '@/components/icons';

interface TenantSummary {
  id: string;
  slug: string;
  brand: string;
  color: string;
  colorSoft: string;
  colorTint: string;
  franchiseType: string;
  royaltyPct: number;
  suspended: boolean;
  subscriptionStatus: string;
  currentPeriodEnd: string | null;
  createdAt: string;
  plan: { code: string; name: string } | null;
  customers: number;
  appointments: number;
  staff: number;
  branches: number;
}

interface Branch {
  id: string;
  name: string;
  short: string;
  country: string;
  region: string;
  active: boolean;
}

interface Plan {
  id: string;
  code: string;
  name: string;
  priceCents: number;
  interval: string;
}

interface TenantInvoice {
  id: string;
  planCode: string | null;
  amountCents: number;
  periodStart: string;
  periodEnd: string;
  status: string;
  issuedAt: string;
  paidAt: string | null;
}

const MODE: Record<string, string> = { direct: '直營', franchise: '加盟', overseas: '海外' };
const FRANCHISE = [
  { value: 'direct', label: '直營' },
  { value: 'franchise', label: '加盟' },
  { value: 'overseas', label: '海外' },
];
const SUB: Record<string, { label: string; tone: 'tag' | 'warn' | 'danger' | 'ink' }> = {
  active: { label: '訂閱中', tone: 'tag' },
  trialing: { label: '試用中', tone: 'ink' },
  past_due: { label: '逾期', tone: 'warn' },
  canceled: { label: '已取消', tone: 'danger' },
};
const ntd = (cents: number) => `NT$${(cents / 100).toLocaleString()}`;

export function PlatformTenantsPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<{ brand: string; email: string } | null>(null);
  const [editing, setEditing] = useState<TenantSummary | null>(null);
  const [planning, setPlanning] = useState<TenantSummary | null>(null);
  const [billing, setBilling] = useState<TenantSummary | null>(null);
  const [branching, setBranching] = useState<TenantSummary | null>(null);

  const tenants = useQuery({ queryKey: ['platform-tenants'], queryFn: () => api.get<TenantSummary[]>('/platform/tenants') });
  const list = tenants.data ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: ['platform-tenants'] });

  const suspend = useMutation({
    mutationFn: (t: TenantSummary) => api.patch(`/platform/tenants/${t.id}`, { suspended: !t.suspended }),
    onSuccess: (_d, t) => { ok(t.suspended ? '已啟用診所' : '已停權診所'); refresh(); },
    onError: fail,
  });

  return (
    <div>
      <PageHeader
        title="租戶總覽"
        subtitle="平台營運方開通與管理白牌租戶"
        right={<button className="btn btn-primary" onClick={() => { setCreated(null); setOpen(true); }}><Icon d={I.plus} size={13} /> 開通新租戶</button>}
      />

      <Banner msg={msg} />

      {created && (
        <div style={{ padding: 14, marginBottom: 16, background: 'var(--success-bg)', borderRadius: 10, fontSize: 13 }}>
          ✅ 已開通「<b>{created.brand}</b>」。請將管理者登入帳號交付客戶:<b className="num">{created.email}</b>(密碼為開通時設定)。
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="租戶總數" value={String(list.length)} tone="primary" />
        <Stat label="停權中" value={String(list.filter((t) => t.suspended).length)} tone={list.some((t) => t.suspended) ? 'danger' : 'ink'} />
        <Stat label="顧客總計" value={String(list.reduce((s, t) => s + t.customers, 0))} tone="ink" />
        <Stat label="員工總計" value={String(list.reduce((s, t) => s + t.staff, 0))} tone="ink" />
      </div>

      <Card title="白牌租戶" icon={<Icon d={I.building} size={14} style={{ color: 'var(--primary)' }} />}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>品牌</th>
              <th style={thStyle}>模式</th>
              <th style={thStyle}>方案 / 訂閱</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>分店</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>顧客</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>員工</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((t) => (
              <tr key={t.id} style={t.suspended ? { opacity: 0.55 } : undefined}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: t.color, flexShrink: 0 }} />
                    <span>
                      {t.brand}
                      {t.suspended && <Tag tone="danger" style={{ marginLeft: 6 }}>停權</Tag>}
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-4)' }} className="num">{t.slug}</span>
                    </span>
                  </span>
                </td>
                <td style={cellStyle}>
                  <Pill bg="var(--surface-2)" color="var(--ink-2)">{MODE[t.franchiseType] ?? t.franchiseType}</Pill>
                  {t.franchiseType !== 'direct' && t.royaltyPct > 0 && <span style={{ fontSize: 11, color: 'var(--ink-4)', marginLeft: 6 }}>分潤 {t.royaltyPct}%</span>}
                </td>
                <td style={cellStyle}>
                  <div style={{ fontSize: 12.5 }}>{t.plan?.name ?? <span style={{ color: 'var(--ink-4)' }}>未指派</span>}</div>
                  <Tag tone={SUB[t.subscriptionStatus]?.tone ?? 'ink'}>{SUB[t.subscriptionStatus]?.label ?? t.subscriptionStatus}</Tag>
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">
                  <button className="btn btn-ghost" style={{ ...rowBtn, marginLeft: 0 }} onClick={() => setBranching(t)}>
                    <Icon d={I.building} size={12} /> {t.branches}
                  </button>
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{t.customers}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{t.staff}</td>
                <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-ghost" style={rowBtn} onClick={() => setEditing(t)}><Icon d={I.edit} size={12} /> 編輯</button>
                  <button className="btn btn-ghost" style={rowBtn} onClick={() => setBranching(t)}><Icon d={I.building} size={12} /> 分店</button>
                  <button className="btn btn-ghost" style={rowBtn} onClick={() => setPlanning(t)}><Icon d={I.package} size={12} /> 方案</button>
                  <button className="btn btn-ghost" style={rowBtn} onClick={() => setBilling(t)}><Icon d={I.wallet} size={12} /> 帳單</button>
                  <button
                    className="btn btn-ghost"
                    style={{ ...rowBtn, color: t.suspended ? 'var(--success)' : 'var(--danger)' }}
                    disabled={suspend.isPending}
                    onClick={() => suspend.mutate(t)}
                  >
                    {t.suspended ? '啟用' : '停用'}
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={7}>尚無租戶,點右上開通</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && (
        <ProvisionModal
          onClose={() => setOpen(false)}
          onDone={(brand, email) => { ok('租戶已開通'); setCreated({ brand, email }); setOpen(false); refresh(); }}
          onFail={fail}
        />
      )}
      {branching && <BranchModal tenant={branching} onClose={() => setBranching(null)} onChanged={refresh} />}
      {editing && <EditModal tenant={editing} onClose={() => setEditing(null)} onDone={() => { ok('租戶設定已更新'); setEditing(null); refresh(); }} onFail={fail} />}
      {planning && <PlanModal tenant={planning} onClose={() => setPlanning(null)} onDone={() => { ok('訂閱方案已更新'); setPlanning(null); refresh(); }} onFail={fail} />}
      {billing && <InvoiceModal tenant={billing} onClose={() => setBilling(null)} onFail={fail} />}
    </div>
  );
}

const rowBtn = { padding: '4px 9px', fontSize: 11, marginLeft: 4 } as const;

/* ───────── 編輯租戶（白牌 / 加盟） ───────── */
function EditModal({ tenant, onClose, onDone, onFail }: { tenant: TenantSummary; onClose: () => void; onDone: () => void; onFail: (e: unknown) => void }) {
  const [f, setF] = useState({
    brand: tenant.brand,
    color: tenant.color,
    colorSoft: tenant.colorSoft,
    colorTint: tenant.colorTint,
    franchiseType: tenant.franchiseType,
    royaltyPct: tenant.royaltyPct,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));

  const save = useMutation({
    mutationFn: () => api.patch(`/platform/tenants/${tenant.id}`, f),
    onSuccess: onDone,
    onError: onFail,
  });

  return (
    <Modal open onClose={onClose} width={560}>
      <ModalHeader title={`編輯租戶 · ${tenant.brand}`} sub={`slug: ${tenant.slug}（slug 不可變更）`} onClose={onClose} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="品牌名稱"><input style={inputStyle} value={f.brand} onChange={(e) => set('brand', e.target.value)} /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <ColorInput label="主色" value={f.color} onChange={(v) => set('color', v)} />
          <ColorInput label="柔色" value={f.colorSoft} onChange={(v) => set('colorSoft', v)} />
          <ColorInput label="淺底" value={f.colorTint} onChange={(v) => set('colorTint', v)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="營運模式"><Select value={f.franchiseType} onChange={(v) => set('franchiseType', v)} options={FRANCHISE} /></Field>
          <Field label="加盟分潤 %"><input type="number" className="num" style={inputStyle} value={f.royaltyPct} onChange={(e) => set('royaltyPct', Math.max(0, Math.min(100, Number(e.target.value) || 0)))} /></Field>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>取消</button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!f.brand.trim() || save.isPending} onClick={() => save.mutate()}><Icon d={I.check} size={13} /> 儲存</button>
      </div>
    </Modal>
  );
}

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ width: 36, height: 34, border: '1px solid var(--line)', borderRadius: 6, background: 'transparent', cursor: 'pointer', flexShrink: 0 }} />
        <input style={{ ...inputStyle, flex: 1, minWidth: 0 }} className="num" value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </Field>
  );
}

/* ───────── 分店管理（平台營運方開立 / 停用） ───────── */
const COUNTRIES = [
  { value: 'TW', label: '台灣' },
  { value: 'HK', label: '香港' },
  { value: 'MO', label: '澳門' },
  { value: 'SG', label: '新加坡' },
  { value: 'CN', label: '中國' },
  { value: 'JP', label: '日本' },
];
const COUNTRY_LABEL: Record<string, string> = Object.fromEntries(COUNTRIES.map((c) => [c.value, c.label]));

function BranchModal({ tenant, onClose, onChanged }: { tenant: TenantSummary; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const branches = useQuery({ queryKey: ['tenant-branches', tenant.id], queryFn: () => api.get<Branch[]>(`/platform/tenants/${tenant.id}/branches`) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['tenant-branches', tenant.id] }); onChanged(); };

  const [nf, setNf] = useState({ name: '', short: '', country: 'TW', region: '' });
  const [editId, setEditId] = useState<string | null>(null);
  const [ef, setEf] = useState({ name: '', short: '', country: 'TW', region: '' });

  const add = useMutation({
    mutationFn: () => api.post(`/platform/tenants/${tenant.id}/branches`, nf),
    onSuccess: () => { ok('已開立分店'); setNf({ name: '', short: '', country: 'TW', region: '' }); refresh(); },
    onError: fail,
  });
  const save = useMutation({
    mutationFn: (id: string) => api.patch(`/platform/branches/${id}`, ef),
    onSuccess: () => { ok('分店已更新'); setEditId(null); refresh(); },
    onError: fail,
  });
  const toggle = useMutation({
    mutationFn: (b: Branch) => api.patch(`/platform/branches/${b.id}`, { active: !b.active }),
    onSuccess: (_d, b) => { ok(b.active ? '已停用分店' : '已啟用分店'); refresh(); },
    onError: fail,
  });

  const rows = branches.data ?? [];
  const startEdit = (b: Branch) => { setEditId(b.id); setEf({ name: b.name, short: b.short, country: b.country, region: b.region }); };

  return (
    <Modal open onClose={onClose} width={640}>
      <ModalHeader title={`分店管理 · ${tenant.brand}`} sub="分店為計費單位，由平台營運方開立與歸戶" onClose={onClose} />
      <div style={{ padding: 20 }}>
        <Banner msg={msg} />

        {/* 開立新分店 */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ flex: '2 1 160px' }}><Field label="分店名稱 *"><input style={inputStyle} value={nf.name} onChange={(e) => setNf((s) => ({ ...s, name: e.target.value }))} placeholder="信義旗艦店" /></Field></div>
          <div style={{ flex: '1 1 80px' }}><Field label="簡稱"><input style={inputStyle} value={nf.short} onChange={(e) => setNf((s) => ({ ...s, short: e.target.value }))} placeholder="信義" /></Field></div>
          <div style={{ flex: '1 1 100px' }}><Field label="國別"><Select value={nf.country} onChange={(v) => setNf((s) => ({ ...s, country: v }))} options={COUNTRIES} /></Field></div>
          <div style={{ flex: '1 1 100px' }}><Field label="地區"><input style={inputStyle} value={nf.region} onChange={(e) => setNf((s) => ({ ...s, region: e.target.value }))} placeholder="台北" /></Field></div>
          <button className="btn btn-primary" disabled={!nf.name.trim() || add.isPending} onClick={() => add.mutate()}><Icon d={I.plus} size={13} /> 開立</button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>分店</th>
              <th style={thStyle}>國別 / 地區</th>
              <th style={thStyle}>狀態</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => editId === b.id ? (
              <tr key={b.id}>
                <td style={cellStyle} colSpan={2}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <input style={{ ...inputStyle, flex: '2 1 120px' }} value={ef.name} onChange={(e) => setEf((s) => ({ ...s, name: e.target.value }))} />
                    <input style={{ ...inputStyle, flex: '1 1 60px' }} value={ef.short} onChange={(e) => setEf((s) => ({ ...s, short: e.target.value }))} placeholder="簡稱" />
                    <div style={{ flex: '1 1 90px' }}><Select value={ef.country} onChange={(v) => setEf((s) => ({ ...s, country: v }))} options={COUNTRIES} /></div>
                    <input style={{ ...inputStyle, flex: '1 1 80px' }} value={ef.region} onChange={(e) => setEf((s) => ({ ...s, region: e.target.value }))} placeholder="地區" />
                  </div>
                </td>
                <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }} colSpan={2}>
                  <button className="btn btn-ghost" style={rowBtn} onClick={() => setEditId(null)}>取消</button>
                  <button className="btn btn-primary" style={rowBtn} disabled={!ef.name.trim() || save.isPending} onClick={() => save.mutate(b.id)}><Icon d={I.check} size={12} /> 儲存</button>
                </td>
              </tr>
            ) : (
              <tr key={b.id} style={b.active ? undefined : { opacity: 0.5 }}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{b.name} <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>{b.short}</span></td>
                <td style={cellStyle}>{COUNTRY_LABEL[b.country] ?? b.country}{b.region ? ` · ${b.region}` : ''}</td>
                <td style={cellStyle}>{b.active ? <Tag tone="tag">營運中</Tag> : <Tag tone="ink">已停用</Tag>}</td>
                <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-ghost" style={rowBtn} onClick={() => startEdit(b)}><Icon d={I.edit} size={12} /> 編輯</button>
                  <button className="btn btn-ghost" style={{ ...rowBtn, color: b.active ? 'var(--danger)' : 'var(--success)' }} disabled={toggle.isPending} onClick={() => toggle.mutate(b)}>{b.active ? '停用' : '啟用'}</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>尚無分店,於上方開立</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>關閉</button>
      </div>
    </Modal>
  );
}

/* ───────── 訂閱方案 ───────── */
function PlanModal({ tenant, onClose, onDone, onFail }: { tenant: TenantSummary; onClose: () => void; onDone: () => void; onFail: (e: unknown) => void }) {
  const plans = useQuery({ queryKey: ['platform-plans'], queryFn: () => api.get<Plan[]>('/platform/plans') });
  const [code, setCode] = useState(tenant.plan?.code ?? '');

  const apply = useMutation({
    mutationFn: () => api.post(`/platform/tenants/${tenant.id}/plan`, { planCode: code }),
    onSuccess: onDone,
    onError: onFail,
  });

  return (
    <Modal open onClose={onClose} width={500}>
      <ModalHeader title={`訂閱方案 · ${tenant.brand}`} sub="指派方案後自動啟用訂閱、設定本期到期日" onClose={onClose} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {plans.isLoading && <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>載入方案…</div>}
        {(plans.data ?? []).map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setCode(p.code)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left',
              padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
              border: `1px solid ${code === p.code ? 'var(--primary)' : 'var(--line)'}`,
              background: code === p.code ? 'var(--primary-tint)' : 'var(--surface)',
            }}
          >
            <span>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</span>
              {tenant.plan?.code === p.code && <Tag tone="ink" style={{ marginLeft: 8 }}>目前</Tag>}
              <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-4)' }} className="num">{p.code}</span>
            </span>
            <b className="num" style={{ color: 'var(--primary-deep)' }}>{ntd(p.priceCents)}<span style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 400 }}>/{p.interval === 'year' ? '年' : '月'}</span></b>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>取消</button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!code || code === tenant.plan?.code || apply.isPending} onClick={() => apply.mutate()}><Icon d={I.check} size={13} /> 指派此方案</button>
      </div>
    </Modal>
  );
}

/* ───────── 帳單 / 收款 ───────── */
function InvoiceModal({ tenant, onClose, onFail }: { tenant: TenantSummary; onClose: () => void; onFail: (e: unknown) => void }) {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const invoices = useQuery({ queryKey: ['tenant-invoices', tenant.id], queryFn: () => api.get<TenantInvoice[]>(`/platform/tenants/${tenant.id}/invoices`) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['tenant-invoices', tenant.id] });

  const gen = useMutation({
    mutationFn: () => api.post(`/platform/tenants/${tenant.id}/invoice`, {}),
    onSuccess: () => { ok('已開立本期帳單'); refresh(); },
    onError: (e) => { fail(e); onFail(e); },
  });
  const pay = useMutation({
    mutationFn: (id: string) => api.post(`/platform/invoices/${id}/pay`, {}),
    onSuccess: () => { ok('已標記收款'); refresh(); },
    onError: fail,
  });

  const rows = invoices.data ?? [];

  return (
    <Modal open onClose={onClose} width={620}>
      <ModalHeader title={`帳單 · ${tenant.brand}`} sub={tenant.plan ? `目前方案：${tenant.plan.name}` : '尚未指派方案（無法開帳單）'} onClose={onClose} />
      <div style={{ padding: 20 }}>
        <Banner msg={msg} />
        <button className="btn btn-primary" style={{ marginBottom: 14 }} disabled={!tenant.plan || gen.isPending} onClick={() => gen.mutate()}>
          <Icon d={I.plus} size={13} /> 開立本期帳單
        </button>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>方案</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>金額</th>
              <th style={thStyle}>期間</th>
              <th style={thStyle}>狀態</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((iv) => (
              <tr key={iv.id}>
                <td style={cellStyle} className="num">{iv.planCode ?? '—'}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{ntd(iv.amountCents)}</td>
                <td style={cellStyle} className="num">{iv.periodStart.slice(0, 10)} ~ {iv.periodEnd.slice(0, 10)}</td>
                <td style={cellStyle}>
                  {iv.status === 'paid'
                    ? <Tag tone="tag">已收款</Tag>
                    : iv.status === 'void' ? <Tag tone="ink">作廢</Tag> : <Tag tone="warn">未收款</Tag>}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {iv.status === 'open' && (
                    <button className="btn btn-ghost" style={rowBtn} disabled={pay.isPending} onClick={() => pay.mutate(iv.id)}><Icon d={I.check} size={12} /> 標記收款</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={5}>尚無帳單</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>關閉</button>
      </div>
    </Modal>
  );
}

/* ───────── 開通新租戶 ───────── */
function ProvisionModal({ onClose, onDone, onFail }: { onClose: () => void; onDone: (brand: string, email: string) => void; onFail: (e: unknown) => void }) {
  const [f, setF] = useState({ brand: '', slug: '', color: '#B97A65', firstBranchName: '', adminName: '', adminEmail: '', adminPassword: '' });
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const create = useMutation({
    mutationFn: () => api.post('/platform/tenants', f),
    onSuccess: () => onDone(f.brand, f.adminEmail),
    onError: onFail,
  });

  const ready = f.brand && /^[a-z0-9-]{2,40}$/.test(f.slug) && f.adminName && /.+@.+\..+/.test(f.adminEmail) && f.adminPassword.length >= 8;

  return (
    <Modal open onClose={onClose} width={560}>
      <ModalHeader title="開通新白牌租戶" sub="建立租戶 + 初始管理者帳號（argon2 雜湊）" onClose={onClose} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <Field label="品牌名稱 *"><input style={inputStyle} value={f.brand} onChange={(e) => set('brand', e.target.value)} placeholder="星漾醫美" /></Field>
          <Field label="代表色"><input type="color" style={{ ...inputStyle, padding: 2, height: 38 }} value={f.color} onChange={(e) => set('color', e.target.value)} /></Field>
        </div>
        <Field label="slug *（網址識別,小寫英數-）"><input style={inputStyle} className="num" value={f.slug} onChange={(e) => set('slug', e.target.value.toLowerCase())} placeholder="star-clinic" /></Field>
        <Field label="首間分店名稱（計費單位,留空預設「總店」）"><input style={inputStyle} value={f.firstBranchName} onChange={(e) => set('firstBranchName', e.target.value)} placeholder="信義旗艦店" /></Field>
        <div style={{ height: 1, background: 'var(--line)', margin: '2px 0' }} />
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>初始管理者</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="姓名 *"><input style={inputStyle} value={f.adminName} onChange={(e) => set('adminName', e.target.value)} placeholder="王院長" /></Field>
          <Field label="Email *"><input style={inputStyle} value={f.adminEmail} onChange={(e) => set('adminEmail', e.target.value)} placeholder="admin@star.tw" autoComplete="off" /></Field>
        </div>
        <Field label="初始密碼 *（≥8 碼）"><input type="password" style={inputStyle} value={f.adminPassword} onChange={(e) => set('adminPassword', e.target.value)} autoComplete="new-password" /></Field>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>取消</button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!ready || create.isPending} onClick={() => create.mutate()}>開通租戶</button>
      </div>
    </Modal>
  );
}
